import {refusedSendReason} from "@agenta/chat/model"
import type {FileUIPart} from "ai"
import {atom} from "jotai"

export interface PendingTask {
    /** The agent to run it with — the session has no turns yet, so nothing else can name it. */
    agentId: string
    text: string
    parts?: FileUIPart[]
    /**
     * The starter template this agent was created from, when it was.
     *
     * The template declares the connections the agent will need, and nothing else on the session
     * route can name it: the agent's config is written by the builder during this very turn, and
     * the URL carries only the agent id. Without it the connect step has nothing to read.
     */
    templateKey?: string
    delivery?: "sending" | "failed"
    /** The refusal's own sentence, when the send was refused with one. */
    failureReason?: string
}

/**
 * The Home composer's hand-off to the chat screen, keyed by the session id Home minted.
 *
 * A brand-new session exists only on this device until its first turn lands, so the message
 * cannot be sent from Home — the conversation engine mounts on the chat route and owns the
 * transport. Home stashes the task here and navigates; the chat screen sends it once and
 * clears the slot.
 */
export const pendingTasksAtom = atom<Record<string, PendingTask>>({})

export const stashPendingTaskAtom = atom(
    null,
    (get, set, {sessionId, task}: {sessionId: string; task: PendingTask}) => {
        set(pendingTasksAtom, {...get(pendingTasksAtom), [sessionId]: task})
    },
)

export const takePendingTaskAtom = atom(null, (get, set, sessionId: string) => {
    const tasks = get(pendingTasksAtom)
    const task = tasks[sessionId]
    if (!task) return null
    const {[sessionId]: _taken, ...rest} = tasks
    set(pendingTasksAtom, rest)
    return task
})

export const failPendingTaskAtom = atom(null, (get, set, sessionId: string) => {
    const tasks = get(pendingTasksAtom)
    const task = tasks[sessionId]
    if (task && task.delivery !== "sending") {
        set(pendingTasksAtom, {...tasks, [sessionId]: {...task, delivery: "failed"}})
    }
})

export const sendPendingTaskAtom = atom(
    null,
    async (
        get,
        set,
        {
            sessionId,
            send,
            retry = false,
        }: {
            sessionId: string
            send: (task: PendingTask) => Promise<void>
            retry?: boolean
        },
    ) => {
        const task = get(pendingTasksAtom)[sessionId]
        if (!task || task.delivery === "sending" || (task.delivery === "failed" && !retry)) return
        const sending: PendingTask = {...task, delivery: "sending"}
        set(pendingTasksAtom, {...get(pendingTasksAtom), [sessionId]: sending})
        try {
            await send(sending)
        } catch (error: unknown) {
            const tasks = get(pendingTasksAtom)
            if (tasks[sessionId] === sending) {
                // Keep the refusal's own sentence with the task: the chat screen is the only
                // surface this send has, so a reason dropped here is a reason the user never sees.
                // Write the slot unconditionally: `sending` carries the PREVIOUS attempt's reason
                // on a retry, and leaving it in place would caption this failure with that one.
                const reason = refusedSendReason(error)
                set(pendingTasksAtom, {
                    ...tasks,
                    [sessionId]: {
                        ...sending,
                        delivery: "failed",
                        failureReason: reason ?? undefined,
                    },
                })
            }
            return
        }
        const tasks = get(pendingTasksAtom)
        if (tasks[sessionId] !== sending) return
        const {[sessionId]: _sent, ...rest} = tasks
        set(pendingTasksAtom, rest)
    },
)
